import { ReactElement } from 'react';
import { match } from 'ts-pattern';
import { InputCardOption } from '~/components/input/InputCards';
import { useZodFormFieldSingleValue } from '~/components/zod/ZodForm';
import { BreOrderContext } from '~/tenants/bre/model/BreOrderContext';
import { BreOrderType } from '~/tenants/bre/model/enums';
import { Outdoor, outdoorSchema } from '~/tenants/bre/performable/matterport/BreMatterportConfig';
import { BreMatterportRate } from '~/tenants/bre/performable/matterport/BreMatterportData';
import BreMatterportJob from '~/tenants/bre/performable/matterport/BreMatterportJob';
import { PersonaType } from '~/tenants/common/TenantPersona';
import { TenantPackageAddOnCards, TenantPackageAddOnCheckboxName } from '~/tenants/common/package';

interface BreMatterportAddOnsProps {
  context: BreOrderContext;
  persona: PersonaType;
}

export default function BreMatterportAddOns({ context, persona }: BreMatterportAddOnsProps): ReactElement | null {
  const { type } = context.metadata;
  const { OUTDOOR_BACK, OUTDOOR_FRONT, OUTDOOR_BOTH, OUTDOOR_BOTH_XL, SITE_PLAN } = BreMatterportRate[type];

  const outdoor = useZodFormFieldSingleValue<Outdoor>('outdoor');

  const outdoorOptions = outdoorSchema.options.map<InputCardOption>((value) => {
      const { lotSqft = 0 } = type === BreOrderType.RESIDENTIAL ? context.metadata : { };

      return {
        title: BreMatterportJob.outdoorName(value),
        cost: match(value)
          .with('back', () => OUTDOOR_BACK)
          .with('front', () => OUTDOOR_FRONT)
          .with('both', () => (lotSqft <= 8000 ? OUTDOOR_BOTH : OUTDOOR_BOTH_XL))
          .exhaustive(),
        value,
      };
  });

  return (
    <>
      {type === BreOrderType.COMMERCIAL && (
        <TenantPackageAddOnCheckboxName
          name="outdoor_commercial"
          title="Outdoors"
          description="Add outdoors scan"
          image="tenant/bre/matterport/outdoor_commercial.jpg"
          persona={persona}
          cost={context.metadata.sqft < 8000 ? OUTDOOR_BOTH : OUTDOOR_BOTH_XL}
        />
      )}
      {type === BreOrderType.RESIDENTIAL && (
        <>
          <TenantPackageAddOnCards
            name="outdoor"
            title="Outdoors Scan"
            description="Add outdoors scan"
            image="tenant/bre/matterport/outdoor.png"
            persona={persona}
            options={outdoorOptions}
          />
          {outdoor === 'both' && (
            <TenantPackageAddOnCheckboxName
              name="site_plan"
              title="Site Plan"
              description="Add a site plan scan"
              image="tenant/bre/matterport/site_plan.jpg"
              persona={persona}
              cost={SITE_PLAN}
            />
          )}
        </>
      )}
    </>
  );
}
